import { useMemo } from 'react';
import { useQuery } from 'data-fetching-client';
import { isFilterEmpty } from 'automation-ui-flow-core/enrollment/enrollmentUtils';
import * as ListTypes from 'automation-ui-flow-core/flow/ListTypes';
import { GET_FLOW_BY_ID, FLOW_BY_ID_FIELDNAME } from 'automation-ui-management/queries/flowQueries';
import { GET_RE_ENROLLMENT_FILTERS_FROM_FILTER_BRANCH, RE_ENROLLMENT_FILTERS_FROM_FILTER_BRANCH_FIELDNAME } from 'automation-ui-management/queries/reenrollmentQueries';
/**
 * Hook that checks if a flow has existing re-enrollment triggers that are unsupported.
 * This logic is used to determine whether to display a warning that those re-enrollment
 * triggers will not be preserved when cloning a flow. It will always return return false
 * there are not existing re-enrollment triggers.
 * @param {flowId} flowId - The ID of the flow to check for supported reenrollment
 * @returns
 */
export const useHasUnsupportedReenrollment = ({
  flowId
}) => {
  const {
    data: flowData,
    error: flowError,
    loading: flowLoading
  } = useQuery(GET_FLOW_BY_ID, {
    variables: {
      flowId
    }
  });
  const flow = flowData === null || flowData === void 0 ? void 0 : flowData[FLOW_BY_ID_FIELDNAME];
  const {
    enrollmentCriteria,
    existingAssociatedLists,
    existingFlowEventFilters,
    existingReenrollmentTriggers,
    existingReEnrollmentTriggerSets,
    isClassicWorkflow
  } = useMemo(() => {
    var _flow$associatedLists, _flow$enrollmentCrite, _flow$enrollmentCrite2, _flow$classicEnrollme, _flow$classicEnrollme2;
    if (flowLoading || !flow) {
      return {
        enrollmentCriteria: null,
        existingAssociatedLists: [],
        existingFlowEventFilters: [],
        existingReenrollmentTriggers: [],
        existingReEnrollmentTriggerSets: [],
        isClassicWorkflow: true
      };
    }
    const triggers = flow && flow.enrollmentCriteria && flow.enrollmentCriteria.triggerType === 'LIST' ? flow.enrollmentCriteria.filterBranch : undefined;
    const associatedLists = (_flow$associatedLists = flow.associatedLists.filter(associatedList => associatedList.listTypes.includes(ListTypes.PERSISTED_RE_ENROLLMENT_LIST))) !== null && _flow$associatedLists !== void 0 ? _flow$associatedLists : [];
    const reenrollmentTriggers = (flow === null || flow === void 0 || (_flow$enrollmentCrite = flow.enrollmentCriteria) === null || _flow$enrollmentCrite === void 0 ? void 0 : _flow$enrollmentCrite.triggerType) === 'LIST' ? flow === null || flow === void 0 || (_flow$enrollmentCrite2 = flow.enrollmentCriteria) === null || _flow$enrollmentCrite2 === void 0 ? void 0 : _flow$enrollmentCrite2.reEnrollmentTriggers : [];
    const reEnrollmentTriggerSets = (_flow$classicEnrollme = (_flow$classicEnrollme2 = flow.classicEnrollmentSettings) === null || _flow$classicEnrollme2 === void 0 ? void 0 : _flow$classicEnrollme2.reEnrollmentTriggerSets) !== null && _flow$classicEnrollme !== void 0 ? _flow$classicEnrollme : [];
    return {
      enrollmentCriteria: !isFilterEmpty(triggers) ? triggers : null,
      existingAssociatedLists: associatedLists || [],
      existingFlowEventFilters: flow.flowEventFilters,
      existingReenrollmentTriggers: reenrollmentTriggers,
      existingReEnrollmentTriggerSets: reEnrollmentTriggerSets,
      isClassicWorkflow: flow.isClassicWorkflow
    };
  }, [flow, flowLoading]);
  const {
    data: reenrollmentSettingsData,
    error: reenrollmentSettingsError,
    loading: reenrollmentSettingsLoading
  } = useQuery(GET_RE_ENROLLMENT_FILTERS_FROM_FILTER_BRANCH, {
    skip: flow === undefined || !!flowError || flowLoading || existingReenrollmentTriggers.length === 0,
    variables: {
      enrollmentCriteria,
      isClassicWorkflow,
      existingAssociatedLists,
      existingFlowEventFilters,
      existingReEnrollmentTriggerSets
    }
  });
  const hasUnsupportedReenrollmentTriggers = useMemo(() => {
    if (reenrollmentSettingsLoading || reenrollmentSettingsError || !reenrollmentSettingsData) {
      return false;
    }
    const reenrollmentSettings = reenrollmentSettingsData[RE_ENROLLMENT_FILTERS_FROM_FILTER_BRANCH_FIELDNAME];
    return reenrollmentSettings.reEnrollmentTriggers.some(trigger => trigger.reEnrollmentTrigger.type === 'NOT_SUPPORTED');
  }, [reenrollmentSettingsData, reenrollmentSettingsError, reenrollmentSettingsLoading]);
  return {
    error: reenrollmentSettingsError,
    loading: reenrollmentSettingsLoading,
    hasUnsupportedReenrollmentTriggers
  };
};